import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { catchError, delay, filter, map, of, switchMap } from 'rxjs';
import { MessagesActions } from './messages.actions';
import { TranslateService } from '@ngx-translate/core';
import {
  selectIsConnected,
  selectIsTransferBetweenPlatforms,
  selectPlatform,
} from '../live-chat/live-chat.reducer';
import { Store } from '@ngrx/store';
import { selectMessagesState } from './messages.reducer';
import { cloneDeep } from '../../../utils/cloneDeep';
import { OverlayActions } from '../overlay-state/overlay.actions';
import { getEndOfChatTextKey } from '../../../utils/endOfChatText';
import { VaDialogService } from '../../services/va-dialog.service';
import { ChatAdminActions } from '../chat-admin/chat-admin.actions';
import {
  selectFirstName,
  selectLastName,
} from '../user-info/user-info.reducer';
import { UserInfoActions } from '../user-info/user-info.actions';
import { selectSessionIdentifier } from '../va-session/va-session.reducer';
import { LiveChatActions } from '../live-chat/live-chat.actions';
import { createPlainSystemMessage } from './helpers/createPlainSystemMessage';
import { createVaFalseInitialMessage } from './helpers/createVaFalseInitialMessage';
import { createLiveChatSystemMessage } from './helpers/createLiveChatSystemMessage';
import { createSimpleMessage } from './helpers/createSimpleMessage';
import { getDelayedMessageTime } from '../../../utils/getDelayedMessageTime';
import {
  ClickableLinkActionType,
  MessageSubType,
  MessageType,
  OverlayType,
  PendingActionType,
  VaFalseCannedResponse,
  vaFalseInitialDialog,
} from '../../types/enum';
import { LiveChatPlatformService } from '../../services/live-chat/live-chat-platform.service';

@Injectable({
  providedIn: 'root',
})
export class MessagesEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private vaDialogService: VaDialogService,
    private liveChatPlatformService: LiveChatPlatformService,
    private translateService: TranslateService
  ) {}

  sendUserMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.handleUserMessage),
      concatLatestFrom(() => [
        this.store.select(selectIsConnected),
        this.store.select(selectFirstName),
        this.store.select(selectLastName),
        this.store.select(selectPlatform),
      ]),
      switchMap(([action, isLiveChat, firstName, lastName, platform]) => {
        const messageText: string = action.query;
        const userMessage = createSimpleMessage({
          text: messageText,
          messageType: MessageType.USER,
        });
        if (isLiveChat) {
          this.liveChatPlatformService
            .getPlatformService(platform)
            .sendMessage({
              message: messageText,
              firstName,
              lastName,
            });
          return of(
            MessagesActions.addSimpleDialogMessage({
              messageText,
              messageType: MessageType.USER,
            })
          );
        } else {
          return of(
            MessagesActions.checkForQuickReply({
              userMessage,
            }),
            MessagesActions.callDialogApi({ query: messageText })
          );
        }
      })
    )
  );

  sendChatMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.callDialogApi),
      concatLatestFrom(() => this.store.select(selectSessionIdentifier)),
      switchMap(([action, sessionId]) =>
        this.vaDialogService
          .sendChatMessage({ query: action.query, sessionId })
          .pipe(
            map((messages: Message[]) => {
              if (messages?.length) {
                return MessagesActions.dialogApiSuccess({ messages: messages });
              } else {
                return MessagesActions.dialogApiFailed();
              }
            }),
            catchError((err) =>
              of(
                MessagesActions.dialogApiFailed(),
                MessagesActions.onDialogApiFailed({ error: err })
              )
            )
          )
      )
    )
  );

  handleDialogResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.dialogApiSuccess),
      switchMap((action) => {
        const messages = action.messages;
        const lastMessage = messages[messages.length - 1];
        if (lastMessage.escalation) {
          if (lastMessage.escalation.intent) {
            return of(
              ChatAdminActions.callCustomerJourney({
                contextName: lastMessage.escalation.intent,
                beforeEscalation: true,
              })
            );
          }
          return of(ChatAdminActions.escalate());
        }
        if (lastMessage.messageSubType === MessageSubType.PAYMENT) {
          return of(
            OverlayActions.showOverlay({ value: OverlayType.CreditCard })
          );
        } else {
          return of(
            // Save message immediately to solve the reloading issue
            MessagesActions.saveMessages({ messages: messages })
          );
        }
      })
    )
  );

  addSavedMessagesWithDelay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.saveMessages),
      switchMap((action) => {
        return of(
          MessagesActions.addMessagesWithDelay({
            messages: action.messages,
            index: 0,
            delay: 0,
          })
        );
      })
    )
  );

  addMessagesWithDelay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.addMessagesWithDelay),
      switchMap((action) => {
        const messages = action.messages;
        const index = action.index;
        const delayedTime = action.delay;
        if (index == messages.length - 1) {
          //last message
          return of(
            MessagesActions.addNewMessages({
              messages: [messages[index]],
              donNotSave: true, // Because the message is already saved
            })
          ).pipe(delay(delayedTime));
        } else {
          const nextDelay = getDelayedMessageTime(messages[index]);
          return of(
            MessagesActions.addNewMessages({
              messages: [messages[index]],
              donNotSave: true, // Because the message is already saved
            }),
            MessagesActions.addMessagesWithDelay({
              messages: messages,
              index: index + 1,
              delay: nextDelay,
            })
          ).pipe(delay(delayedTime));
        }
      })
    )
  );

  updateQuickReplyOrAddUserMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.checkForQuickReply),
      concatLatestFrom(() => this.store.select(selectMessagesState)),
      switchMap(([action, messages]) => {
        // Clone the readonly object
        const updatedMessage = cloneDeep(messages[messages.length - 1]);
        if (
          updatedMessage?.isQuickReply &&
          updatedMessage?.messageComponent?.componentData
        ) {
          updatedMessage.messageComponent.componentData.isCannedResponseVisible =
            false;
          return of(
            MessagesActions.updateQuickReply({
              updatedMessage,
              userMessage: action.userMessage,
            })
          );
        }
        return of(
          MessagesActions.addNewMessages({ messages: [action.userMessage] })
        );
      })
    )
  );

  addUserMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.updateQuickReply),
      map((action) =>
        MessagesActions.addNewMessages({
          messages: [action.userMessage],
        })
      )
    )
  );

  createLiveChatFirstSystemMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.setConnection),
      concatLatestFrom(() => [
        this.store.select(selectFirstName),
        this.store.select(selectLastName),
        this.store.select(selectSessionIdentifier),
        this.store.select(selectPlatform),
      ]),
      map(([, firstName, lastName, sessionId, platform]) => {
        const initialMessage = this.translateService.instant(
          'CHAT.TRANSFER_INITIAL_MESSAGE'
        );
        this.liveChatPlatformService.getPlatformService(platform).sendMessage({
          message: initialMessage,
          firstName,
          lastName,
          sessionId,
          isFirstConnectionMessage: true,
        });
        return MessagesActions.createWaitForConnectionMessage();
      })
    )
  );

  createWaitForConnectionMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.createWaitForConnectionMessage),
      concatLatestFrom(() =>
        this.store.select(selectIsTransferBetweenPlatforms)
      ),
      filter(([, isTransferBetweenPlatforms]) => !isTransferBetweenPlatforms),
      map(() => {
        const systemMessageTitle =
          this.translateService.instant('CHAT.TRANSFERRING');
        const patienceTitle = this.translateService.instant('CHAT.PATIENCE');
        const cancelTransfer = this.translateService.instant(
          'CHAT.CANCEL_TRANSFER'
        );
        const firstMessage = createLiveChatSystemMessage(
          systemMessageTitle,
          patienceTitle,
          cancelTransfer,
          true
        );
        return MessagesActions.addNewMessages({
          messages: [firstMessage],
        });
      })
    )
  );

  // Listen to Operator Message
  addPlainSystemMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.addPlainSystemMessage),
      switchMap((action) => {
        const message = createPlainSystemMessage({
          messageId: action.messageId,
          messageTitle: action.text,
          messageSubType: action.messageSubtype,
        });
        return of(MessagesActions.addNewMessages({ messages: [message] }));
      })
    )
  );

  addEndLiveChatMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.addEndOfChatMessage),
      switchMap((action) => {
        const messageText = this.translateService.instant(
          getEndOfChatTextKey(action.reason)
        );
        return of(
          MessagesActions.addSimpleDialogMessage({
            messageText,
            messageType: MessageType.VA,
          })
        );
      })
    )
  );

  handleCannedResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.handleCannedResponse),
      concatLatestFrom(() => this.store.select(selectIsConnected)),
      filter(([, isLiveChat]) => !isLiveChat),
      switchMap(([action]) => {
        if (action.response.response === vaFalseInitialDialog) {
          return of(
            MessagesActions.onVaFalseCannedResponseClick({
              response: action.response,
            })
          );
        }
        return of(
          MessagesActions.handleUserMessage({ query: action.response.value })
        );
      })
    )
  );

  handleClickableLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.handleClickableLink),
      concatLatestFrom(() => this.store.select(selectIsConnected)),
      filter(([, isLiveChat]) => !isLiveChat),
      switchMap(([action]) => {
        const bubbleEventLink = action.bubbleEvent.link;
        if (bubbleEventLink?.action) {
          const clickableAction: ClickableLinkAction = bubbleEventLink?.action;
          if (clickableAction.type == ClickableLinkActionType.SIGN_IN_NO) {
            return of(
              MessagesActions.callDialogApi({ query: clickableAction.response })
            );
          }
          if (clickableAction.type == ClickableLinkActionType.SIGN_IN_YES) {
            return of(
              UserInfoActions.signIn({
                pendingAction: {
                  type: PendingActionType.callDialogApi,
                  parameters: clickableAction.response,
                },
              })
            );
          }
        } else if (bubbleEventLink?.url) {
          const target = bubbleEventLink.isNewTab ? '_blank' : '_top';
          window.open(bubbleEventLink.url, target);
        }
      })
    )
  );

  createLiveChatSystemMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.updateLiveChatSystemMessage),
      concatLatestFrom(() => this.store.select(selectMessagesState)),
      switchMap(([action, messages]) => {
        const updatedMessage: Message = cloneDeep(
          messages[messages.length - 1]
        );
        if (
          updatedMessage.messageType == MessageType.SYSTEM &&
          updatedMessage.messageComponent?.componentData
        ) {
          if (action.text) {
            updatedMessage.messageComponent.componentData.cardText =
              action.text;
          }
          if (action.queuePosition) {
            updatedMessage.messageComponent.componentData.queuePosition =
              action.queuePosition;
          }
          if (action.estimatedTime) {
            updatedMessage.messageComponent.componentData.estimatedTime =
              action.estimatedTime;
          }

          return of(
            MessagesActions.updateLastMessage({
              updatedMessage: updatedMessage,
            })
          );
        } else {
          const message = createLiveChatSystemMessage(action.text);
          return of(
            MessagesActions.addNewMessages({
              messages: [message],
            })
          );
        }
      })
    )
  );

  addSimpleDialogMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.addSimpleDialogMessage),
      switchMap((action) => {
        const message = createSimpleMessage({
          messageId: action.messageId,
          text: action.messageText,
          messageType: action.messageType,
          operatorName: action.operatorName,
          messageSubType: action.messageSubType,
        });
        return of(MessagesActions.addNewMessages({ messages: [message] }));
      })
    )
  );

  addPostEscalationSelectionMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.addPostEscalationMessageSelection),
      switchMap((action) => {
        const requesting = this.translateService.instant('CHAT.REQUESTING');
        let messageText = `${requesting}: ${action.postEscalationSelection.category}`;
        if (action.postEscalationSelection.product) {
          messageText += `-${action.postEscalationSelection.product}`;
        }
        if (action.postEscalationSelection.specificProduct) {
          messageText += `-${action.postEscalationSelection.specificProduct}`;
        }
        return of(
          MessagesActions.addSimpleDialogMessage({
            messageText,
            messageType: MessageType.USER,
          })
        );
      })
    )
  );

  addVaFalseDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.askUserToLogin),
      switchMap(() => {
        return of(
          MessagesActions.addNewMessages({
            messages: [createVaFalseInitialMessage(this.translateService)],
          })
        );
      })
    )
  );

  onVaFalseCannedResponseClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.onVaFalseCannedResponseClick),
      switchMap((action) => {
        const messsage = createSimpleMessage({
          text: action.response.value,
          messageType: MessageType.USER,
        });
        switch (action.response.key) {
          case VaFalseCannedResponse.SIGN_INTO_ACCOUNT: {
            return of(
              MessagesActions.checkForQuickReply({
                userMessage: messsage,
              }),
              UserInfoActions.signIn({
                pendingAction: {
                  type: PendingActionType.escalateToLiveChat,
                  parameters: null,
                },
              })
            );
          }
          case VaFalseCannedResponse.UNABLE_TO_SIGN_IN: {
            return of(
              MessagesActions.checkForQuickReply({
                userMessage: messsage,
              }),
              ChatAdminActions.escalate()
            );
          }
          case VaFalseCannedResponse.NOT_A_CUSTOMER: {
            return of(
              MessagesActions.checkForQuickReply({
                userMessage: messsage,
              }),
              ChatAdminActions.callCustomerJourney({
                contextName: VaFalseCannedResponse.CONTEXT_VALUE,
              })
            );
          }
        }
      })
    )
  );
}
